// @flow
import React, { Component } from 'react';
import 'app/common/react/components/profile/showcase/ShowcaseCarousel/ShowcaseCarousel.scss';
import CONST from 'app/common/lib/Const';
import PropTypes from 'prop-types';
import ShowcaseContent from 'app/common/react/components/profile/showcase/ShowcaseContent/ShowcaseContent';
import { ShowcaseShopProps } from 'app/common/react/components/profile/showcase/ShopShowcaseSection/ShopShowcaseSection';
import { track } from 'app/common/lib/react/analytics';
import type {
  AnimationDirection,
  ShowcaseSectionType,
} from 'app/common/react/components/profile/showcase/commonTypes';
import {
  ANIMATION_DIRECTION,
  ShowcaseArticleProps,
  ShowcaseBoardProps,
  type ShowcaseBoardType,
  type ShowcaseArticleType,
  type ShowcaseShopType,
} from 'app/common/react/components/profile/showcase/commonTypes';

const LEFT_SECTION_CLASSES: string = 'leftSectionClasses';
const MAIN_SECTION_CLASSES: string = 'mainSectionClasses';
const RIGHT_SECTION_CLASSES: string = 'rightSectionClasses';

type ShowcaseCarouselType = {
  animationDirection: ?AnimationDirection,
  currentIndex: number,
  onEmptyStateDismissal: Function,
  sections: Array<?ShowcaseSectionType>,
  viewingOwnProfile: boolean,
};

export class ShowcaseCarousel extends Component<ShowcaseCarouselType> {
  componentDidMount() {
    const { currentIndex } = this.props;
    this.logShowcaseSectionEvent(currentIndex, PCONST.thrift.EventType.VIEW);
  }

  getSectionClassNames: () => Object = () => {
    const { animationDirection } = this.props;

    let leftSectionClasses, mainSectionClasses, rightSectionClasses;
    if (animationDirection === ANIMATION_DIRECTION.LEFT) {
      leftSectionClasses = 'absolute hidden';
      mainSectionClasses = 'absolute mainSectionSlideFromLeft';
      rightSectionClasses = 'absolute fadeRightOut';
    } else if (animationDirection === ANIMATION_DIRECTION.RIGHT) {
      leftSectionClasses = 'absolute fadeLeftOut';
      mainSectionClasses = 'absolute mainSectionSlideFromRight';
      rightSectionClasses = 'absolute hidden';
    } else {
      leftSectionClasses = 'absolute hidden';
      mainSectionClasses = 'absolute';
      rightSectionClasses = 'absolute hidden';
    }
    return {
      [LEFT_SECTION_CLASSES]: leftSectionClasses,
      [MAIN_SECTION_CLASSES]: mainSectionClasses,
      [RIGHT_SECTION_CLASSES]: rightSectionClasses,
    };
  };

  getIndexesFromSection: (
    currentIndex: number,
    sections: Array<?(ShowcaseArticleType | ShowcaseBoardType | ShowcaseShopType)>,
  ) => Object = (currentIndex, sections) => {
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : sections.length - 1;
    const nextIndex = (currentIndex + 1) % sections.length;

    return { prevIndex, nextIndex };
  };

  logShowcaseSectionEvent: (
    sectionIndex: number,
    eventType: PCONST.thrift.EventType.VIEW | PCONST.thrift.EventType.CLICK,
  ) => void = (sectionIndex, eventType) => {
    const { sections } = this.props;
    const section = sections[sectionIndex];
    if (!section) {
      return;
    }
    let elementType = '',
      sectionId = '';
    switch (section.type) {
      case CONST.SHOWCASE_TYPE_BOARD:
        elementType = PCONST.thrift.ElementType.SHOWCASE_BOARD;
        sectionId = section.id;
        break;
      case CONST.SHOWCASE_TYPE_ARTICLE:
        elementType = PCONST.thrift.ElementType.SHOWCASE_ARTICLE;
        sectionId = section.id;
        break;
      case CONST.SHOWCASE_TYPE_SHOP:
        elementType = PCONST.thrift.ElementType.SHOWCASE_SHOP_SPACE;
        const { link_data } = section;
        sectionId = link_data.id;
        break;
      default:
        break;
    }
    track(eventType, {
      view: PCONST.thrift.ViewType.USER,
      viewParameter: PCONST.thrift.ViewParameterType.USER_BOARDS,
      component: PCONST.thrift.ComponentType.SHOWCASE,
      element: elementType,
      index: sectionIndex,
      section_id: sectionId,
    });
  };

  render() {
    const { currentIndex, onEmptyStateDismissal, sections, viewingOwnProfile } = this.props;
    const { prevIndex, nextIndex } = this.getIndexesFromSection(currentIndex, sections);
    const sectionClassNames = this.getSectionClassNames();
    return (
      <div className="ShowcaseCarousel rounded">
        {sections.length > 1 && (
          <div className={sectionClassNames[LEFT_SECTION_CLASSES]} key={prevIndex}>
            <ShowcaseContent
              currentSection={sections[prevIndex]}
              viewingOwnProfile={viewingOwnProfile}
            />
          </div>
        )}
        <div className={sectionClassNames[MAIN_SECTION_CLASSES]} key={currentIndex}>
          <ShowcaseContent
            {...this.props}
            currentSection={sections[currentIndex]}
            onEmptyStateDismissal={onEmptyStateDismissal}
            viewingOwnProfile={viewingOwnProfile}
          />
        </div>
        {sections.length > 2 && (
          <div className={sectionClassNames[RIGHT_SECTION_CLASSES]} key={nextIndex}>
            <ShowcaseContent
              currentSection={sections[nextIndex]}
              viewingOwnProfile={viewingOwnProfile}
            />
          </div>
        )}
      </div>
    );
  }
}

ShowcaseCarousel.propTypes = {
  animationDirection: PropTypes.string,
  currentIndex: PropTypes.number.isRequired,
  onEmptyStateDismissal: PropTypes.func.isRequired,
  sections: PropTypes.arrayOf(
    PropTypes.oneOfType([ShowcaseArticleProps, ShowcaseBoardProps, ShowcaseShopProps]),
  ),
  viewingOwnProfile: PropTypes.bool.isRequired,
};

export default ShowcaseCarousel;
